import { Radio } from '@cluey/cluey-components';
import { useId } from 'react';

export type RadioGroupOptionPassdownProps = {
  id: string;
  groupName: string;
  value: string;
  checked: boolean;
  error: boolean;
  disabled: boolean;
};

export type RadioGroupOptionProps = Omit<RadioGroupOptionPassdownProps, 'id'> & {
  containerClassName?: string;
  onChange: (value: string) => void;
  renderOption: (passdownProps: RadioGroupOptionPassdownProps) => React.ReactNode;
  renderBelowOption?: (passdownProps: RadioGroupOptionPassdownProps) => React.ReactNode;
};

const RadioGroupOption = (props: RadioGroupOptionProps) => {
  const id = useId();
  const { groupName, renderOption, renderBelowOption, value, checked, error, disabled, onChange, containerClassName } =
    props;
  return (
    <div className={containerClassName}>
      <div className="flex items-center">
        <Radio
          value={value}
          onChange={onChange}
          group={groupName}
          checked={checked}
          error={error}
          id={id}
          disabled={disabled}
        />
        <label htmlFor={id} className="flex cursor-pointer flex-col items-start pl-4">
          {renderOption({ id, groupName, value, checked, error, disabled })}
        </label>
      </div>
      {renderBelowOption && renderBelowOption({ id, groupName, value, checked, error, disabled })}
    </div>
  );
};

export type RadioGroupOption = {
  value: string;
  labelForDropdown?: string;
  renderOption: (props: RadioGroupOptionPassdownProps) => React.ReactNode;
  renderBelowOption?: (props: RadioGroupOptionPassdownProps) => React.ReactNode;
  renderBelowDropdownOption?: (props: RadioGroupOptionPassdownProps) => React.ReactNode;
};

export type RadioGroupProps = {
  options: RadioGroupOption[];
  name: string;
  value: string;
  onChange: (value: string) => void;
};

const RadioGroup = ({ options, name, value, onChange }: RadioGroupProps) => {
  return (
    <div>
      {options.map((option, index) => (
        <RadioGroupOption
          key={option.value}
          containerClassName={index > 0 ? 'mt-5' : undefined}
          groupName={name}
          renderOption={option.renderOption}
          renderBelowOption={option.renderBelowOption}
          value={option.value}
          checked={value === option.value}
          error={false}
          disabled={false}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

export default RadioGroup;
